import { useIsomorphicLayoutEffect } from '@jusbrasil-web/shared-utils-events'
import classNames from 'classnames'
import { useRef } from 'react'

import styles from './mount-animation.module.scss'

interface MountAnimationProps {
  visible: boolean
  children: React.ReactNode
}

// Animates height to prevent Cumulative Layout Shift (CLS) when components appear/disappear
// Works alongside internal Banner opacity transition for smooth user experience
export const MountAnimation = ({ visible, children }: MountAnimationProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const refreshHeight = () => {
    if (!contentRef.current || !containerRef.current) return
    const height = contentRef.current.scrollHeight
    containerRef.current.style.setProperty('--dynamic-max-height', `${height}px`)
  }

  useIsomorphicLayoutEffect(() => {
    const contentElement = contentRef.current
    if (!contentElement) return

    const resizeObserver = new ResizeObserver(refreshHeight)
    resizeObserver.observe(contentElement)

    refreshHeight()

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useIsomorphicLayoutEffect(refreshHeight, [children, visible])

  const containerClasses = classNames(styles.mountAnimation, {
    [styles.visible]: visible,
    [styles.hidden]: !visible,
  })

  return (
    <div ref={containerRef} className={containerClasses}>
      <div ref={contentRef} className={styles.content}>
        {children}
      </div>
    </div>
  )
}
